
import { useEffect, useState, useCallback, useMemo } from "react";
import { useGmailFetch } from "./useGmailFetch";
import { EmailListStates } from "./EmailListStates";
import { EmailListScroll } from "./EmailListScroll";
import { Email } from "@/types/email";
import { useQueryClient } from "@tanstack/react-query";

interface EmailListProps {
  onSelectEmail: (email: Email | null) => void;
  onSelectMultiple: (emails: Email[]) => void;
  selectedInbox?: string;
}

const EmailList = ({ onSelectEmail, onSelectMultiple, selectedInbox }: EmailListProps) => {
  const [selectedEmailIds, setSelectedEmailIds] = useState<Set<string>>(new Set());
  const [shouldUpdateSelection, setShouldUpdateSelection] = useState<boolean>(false);
  const queryClient = useQueryClient();
  
  const { 
    data, 
    isLoading, 
    error,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isError,
    refetch
  } = useGmailFetch(selectedInbox);

  // Memoize the emails array to prevent unnecessary recomputations
  const allEmails = useMemo(() => {
    return data?.pages.flatMap(page => page.emails) || [];
  }, [data]);

  // Optimize the selection handling by batching updates
  const handleEmailSelect = useCallback((email: Email, checked: boolean) => {
    setSelectedEmailIds(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(email.id);
      } else {
        newSet.delete(email.id);
      }
      return newSet;
    });
    // Flag that selection has changed and needs processing
    setShouldUpdateSelection(true);
  }, []);

  // Memoize the selected emails for stability
  const selectedEmailObjects = useMemo(() => {
    if (selectedEmailIds.size === 0) return [];
    return allEmails.filter(e => selectedEmailIds.has(e.id));
  }, [selectedEmailIds, allEmails]);

  // Use a debounced effect for updating selected emails to reduce renders
  useEffect(() => {
    if (!shouldUpdateSelection) return;

    const handler = setTimeout(() => {
      if (selectedEmailObjects.length === 1) {
        onSelectEmail(selectedEmailObjects[0]);
        onSelectMultiple([selectedEmailObjects[0]]);
      } else if (selectedEmailObjects.length > 1) {
        onSelectEmail(null);
        onSelectMultiple(selectedEmailObjects);
      } else {
        onSelectEmail(null);
        onSelectMultiple([]);
      }
      setShouldUpdateSelection(false);
    }, 100); // Small delay to batch multiple selection changes

    return () => clearTimeout(handler);
  }, [selectedEmailObjects, onSelectEmail, onSelectMultiple, shouldUpdateSelection]);

  // Reset selection when inbox changes
  useEffect(() => {
    setSelectedEmailIds(new Set());
    setShouldUpdateSelection(false);
  }, [selectedInbox]);

  const handleReconnectSuccess = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['emailAccounts'] });
    refetch();
  }, [queryClient, refetch]);

  const statesComponent = (
    <EmailListStates
      isLoading={isLoading}
      isError={isError}
      error={error as Error}
      selectedInbox={selectedInbox}
      onReconnectSuccess={handleReconnectSuccess}
    />
  );
  
  if (!selectedInbox || isLoading || isError || error) {
    return statesComponent;
  }

  return (
    <EmailListScroll
      emails={allEmails}
      selectedEmailIds={selectedEmailIds}
      onSelect={handleEmailSelect}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      fetchNextPage={fetchNextPage}
    />
  );
};

export default EmailList;
